import Grid from '@mui/material/Grid';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import W86AutoComplete from '../Component/W86AutoComplete';
import {
  ComponentFormContainer,
  CustomizeProvider,
  FrontendClientProvider,
} from '@pipedream/connect-react';
import { createFrontendClient } from '@pipedream/sdk/browser';
import { send_component_save_request, send_request } from '../../utils/Request';
import { useParams } from 'react-router';
import config from '../../config';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import checkKey from '../../utils/CheckKey';
import { v4 as uuidv4 } from 'uuid';
import { Button, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './TriggerFromExternalTemplate.css';
import { retrieveConnectToken } from '../../utils/PipeDreamUtil';
import CustomPaper from '../common/CustomPaper';
import pipedreamCustomiseConfig from './PipedreamCustomiseConfig';
import { styled } from '@mui/material/styles';
import useTriggerFromExternalStore, { getTriggerFromExternalStore } from './useTriggerFromExternalStore';
import UpgradeBanner from './UpgradeBanner';

const generateOutputPlaceholderKey = () => {
  return `external_trigger_${uuidv4().split('-')[0]}`;
};

const LoadingContainer = styled(Grid)(() => ({
  minHeight: 300,
}));

const LoadingItem = styled(Grid)(() => ({
  width: '100%',
}));

const LoadingSkeleton = styled(Skeleton)(() => ({
  borderRadius: '8px',
  width: '100%',
  height: 300,
}));

const TriggerFromExternalTemplate = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  const [configuredProps, setConfiguredProps] = useState({});
  const [isFirstPipedreamFormUpdate, setIsFirstPipedreamFormUpdate] = useState(true);
  const [appOptions, setAppOptions] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [appTriggerOptions, setAppTriggerOptions] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState({});
  const [triggerId, setTriggerId] = useState('');
  const [outputPlaceholderKey, setOutputPlaceholderKey] = useState('');
  const [outputPlaceholderError, setOutputPlaceholderError] = useState('');
  const [bannerAnimating, setBannerAnimating] = useState(false);
  const [triggerExternalComponentData, setTriggerExternalComponentData] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);

  const store = useMemo(() => getTriggerFromExternalStore(props.component_id), [props.component_id]);

  const retrieveComponentData = (componentResponse) => {
    return componentResponse?.data?.components[0]?.componentData;
  };

  const isNotCustomUser = () => {
    const userTier = useProjectStore.getState().tier;
    return userTier !== 'CUSTOM';
  };

  const animateUpgradeBanner = () => {
    setBannerAnimating(true);
    setTimeout(() => setBannerAnimating(false), 600);
  };

  const pipeDreamClient = createFrontendClient({
    environment: config.PIPEDREAM_ENV,
    externalUserId: clientId,
    tokenCallback: () => retrieveConnectToken(clientId),
  });

  const isOnlyOptionalPropsRemoved = (newConfiguredProp) => {
    const currentKeys = Object.keys(configuredProps || {});
    const newKeys = Object.keys(newConfiguredProp || {});

    const removedKeys = currentKeys.filter((key) => !newKeys.includes(key));

    const optionalPropNames = (selectedTrigger?.configurableProps || [])
      .filter((prop) => prop.optional)
      .map((prop) => prop.name);

    return removedKeys.length > 0 && removedKeys.every((key) => optionalPropNames.includes(key));
  };

  const outputPlaceholderOnChange = (event) => {
    if (isNotCustomUser()) return;
    const val = replacePlaceholderValue(event.target.value);
    const check = checkKey(val);
    if (Array.isArray(check) && check.length > 1 && check[0] === true) {
      setOutputPlaceholderError(check[1]);
    } else setOutputPlaceholderError('');

    setOutputPlaceholderKey(event.target.value);
  };

  useEffect(() => {
    const projectId = params.id;
    send_request(`authz-service/get_client_by_project/${projectId}`, '', {}, 'GET').then(
      (response) => {
        const retrievedClientId = response?.data;
        if (retrievedClientId) {
          setClientId(retrievedClientId);
        } else {
          console.error('Failed to retrieve client id');
        }
      }
    );
  }, [params]);

  useEffect(() => {
    if (store.getState().loaded) {
      setInitialLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setInitialLoading(true);

    Promise.all([
      send_request(`ai-service/pipedream/getAllApps`, '', {}, 'GET'),
      send_request(
        `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
        '',
        {}
      ),
    ])
      .then(([appsResponse, componentResponse]) => {
        const listOfPipedreamApps = appsResponse?.data?.data;
        setAppOptions(listOfPipedreamApps);
        let triggerFromExternalData = retrieveComponentData(componentResponse);
        if (triggerFromExternalData) {
          setTriggerExternalComponentData(triggerFromExternalData);
        } else {
          setTriggerExternalComponentData({});
        }
        const selectedAppNameSlug = triggerFromExternalData?.selectedAppNameSlug;

        if (selectedAppNameSlug) {
          const selectedAppObject = listOfPipedreamApps?.find(
            (appObject) => appObject.nameSlug === selectedAppNameSlug
          );
          setSelectedApp(selectedAppObject || {});
        }
        setSelectedTrigger(triggerFromExternalData?.selectedTriggerKey || {});
        setConfiguredProps(triggerFromExternalData?.configuredProps || {});
        setTriggerId(triggerFromExternalData?.triggerId || '');
        setOutputPlaceholderKey(
          triggerFromExternalData?.outputPlaceholderKey || generateOutputPlaceholderKey()
        );
      })
      .finally(() => {
        store.setState({ loaded: true });
        props.setChildTemplateLoaded(true);
        setInitialLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedApp) {
      return;
    }
    setTriggersLoading(true);
    send_request('ai-service/pipedream/getTriggers/' + selectedApp.nameSlug).then((response) => {
      const responseData = response?.data;
      if (responseData) {
        const triggers = responseData?.data;
        setAppTriggerOptions(triggers);
        setTriggersLoading(false);

        if (typeof selectedTrigger === 'string') {
          setSelectedTrigger(triggers.find((trigger) => trigger.key === selectedTrigger) || {});
        }
      }
    });
  }, [selectedApp]);

  const handleSave = useCallback(() => {
    let cData = triggerExternalComponentData || {};

    if (triggerExternalComponentData) {
      cData.selectedAppNameSlug = selectedApp?.nameSlug || '';
      cData.selectedTriggerKey = selectedTrigger?.key || '';
      cData.configuredProps = configuredProps || {};
      cData.triggerId = triggerId || '';
      cData.outputPlaceholderKey = outputPlaceholderKey || '';
    } else {
      cData = store.getState().getTemplate();
    }

    const componentName = store.getState().name;
    const componentDescription = store.getState().componentDescription;
    let lastModified = getCurrentTime();
    let data = injectMetadataFields({
      componentData: {
        ...cData,
        name: componentName,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: 'trigger_from_external_app',
      description: componentDescription,
    });
    send_component_save_request(
      'component-service/trigger_from_external_app/data',
      data,
      '',
      'POST'
    )
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(data);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }, [
    triggerExternalComponentData,
    selectedApp,
    selectedTrigger,
    configuredProps,
    triggerId,
    outputPlaceholderKey,
    props.component_id,
  ]);

  const setConfiguredPropsAfterFormIsLoaded = (newConfiguredProp) => {
    if (!isFirstPipedreamFormUpdate) {
      setConfiguredProps(newConfiguredProp);
      return;
    }
    const onlyRemovedOptional = isOnlyOptionalPropsRemoved(newConfiguredProp);

    if (onlyRemovedOptional && isFirstPipedreamFormUpdate) {
      setIsFirstPipedreamFormUpdate(false);
    } else {
      setConfiguredProps(newConfiguredProp);
    }
  };

  return (
    <CustomPaper className="main-content">
      {initialLoading ? (
        <LoadingContainer
          container
          direction="column"
          spacing={1}
          justifyContent="center"
          alignItems="center"
        >
          <LoadingItem item xs={12}>
            <LoadingSkeleton variant="rectangular" />
          </LoadingItem>
        </LoadingContainer>
      ) : (
        <>
          {isNotCustomUser() && (
            <UpgradeBanner
              animating={bannerAnimating}
              onUpgradeClick={() => navigate('/organization')}
            />
          )}

          <Grid
            container
            direction="column"
            spacing={1}
            className={isNotCustomUser() ? 'disabled' : ''}
          >
            <Grid item>
              <W86AutoComplete
                title={'Select an App'}
                value={selectedApp}
                inputPlaceholder={'Search for an app...'}
                onChange={(e, newValue) => {
                  if (isNotCustomUser()) {
                    animateUpgradeBanner();
                    return;
                  }
                  setSelectedApp(newValue);
                  setSelectedTrigger({});
                  if (newValue) {
                    setTriggersLoading(true);
                  }
                }}
                options={appOptions}
                renderOptionLabel={(option) => option?.name || ''}
                renderOptionIcon={(option) => option?.imgSrc}
                sx={{ mb: 1 }}
                disabled={false}
              />
            </Grid>
            <Grid item>
              {selectedApp && !triggersLoading && (
                <W86AutoComplete
                  title={'Select an Event'}
                  value={selectedTrigger}
                  onChange={(e, newValue) => {
                    if (isNotCustomUser()) return;
                    setSelectedTrigger(newValue);
                    setConfiguredProps({});
                  }}
                  options={appTriggerOptions}
                  inputPlaceholder={'Search for an event...'}
                  renderOptionLabel={(option) => option?.name || ''}
                  disabled={!selectedApp || isNotCustomUser()}
                  sx={{ mb: 1 }}
                />
              )}
              {triggersLoading && (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  sx={{ mb: 1, borderRadius: '4px' }}
                />
              )}
            </Grid>
            {selectedTrigger?.key && !isNotCustomUser() && (
              <Grid item sx={{ mb: 1 }}>
                <FrontendClientProvider client={pipeDreamClient}>
                  <CustomizeProvider {...pipedreamCustomiseConfig}>
                    <ComponentFormContainer
                      userId={clientId}
                      componentKey={selectedTrigger?.key}
                      configuredProps={configuredProps}
                      onUpdateConfiguredProps={setConfiguredPropsAfterFormIsLoaded}
                    />
                  </CustomizeProvider>
                </FrontendClientProvider>
              </Grid>
            )}

            <Grid item>
              <Typography className="output-placeholder-label">Output Placeholder</Typography>
              <W86PlaceholderTextField
                value={outputPlaceholderKey}
                placeholderText="Enter an output placeholder"
                error={outputPlaceholderError}
                customStyleProps={{ width: '100%' }}
                handleChange={outputPlaceholderOnChange}
                disabled={isNotCustomUser()}
              />
            </Grid>
          </Grid>
        </>
      )}
    </CustomPaper>
  );
};

export default TriggerFromExternalTemplate;
